import Tooltip from "@/components/atoms/Tooltip/Tooltip";
import { InfoIcon, evolutionCheckMarkIcon } from "@/designSystemIcons";
import { Cost, Requirement, Upgrade } from "@/redux/evolutions/evolutionsSlice";
import { showModal } from "@/redux/message/messageSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { BREAKPOINTS } from "@/settings";
import clsx from "clsx";
import { PauseCircle } from "lucide-react";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import {
  checkmark,
  expire as expireIcon,
  info as infoIcon,
} from "../../../../icons";
import LoadingDots from "../../../atoms/LoadingDots/LoadingDots";
import { convertCost } from "../EvolutionsNode/EvolutionsNode";

export type OnHandleCompleteProps = ({
  id,
  categoryId,
  completed,
}: {
  id: number;
  categoryId: number;
  completed: boolean;
}) => void;

type Props = {
  title: string;
  isActive: boolean;
  expanded?: boolean;
  requirements: Requirement[];
  upgrades: Upgrade[];
  handleClick: () => void;
  timeRemaining: string;
  newEvolution: boolean;
  processingState: 0 | 1 | 2 | 3;
  cost?: Cost;
  id?: number;
  categoryId?: number;
  completed?: boolean;
  onActivateEvolution?: () => void;
  onHandleComplete: OnHandleCompleteProps;
};

const EvolutionNode = ({ label }: { label: string }) => (
  <div
    className="px-1 text-white"
    style={{
      borderRadius: "7.2px",
      border: "0.9px solid #ECD882",
      background:
        "radial-gradient(129.93% 211.88% at 38.3% 49.85%, rgba(1, 33, 22, 0.88) 0%, #044738 48.96%, #032D23 100%), #D9D9D9",
    }}
  >
    {label}
  </div>
);

const EvolutionsCard = ({
  title,
  expanded,
  isActive,
  newEvolution,
  requirements,
  upgrades,
  timeRemaining,
  processingState,
  cost,
  id,
  categoryId,
  completed,
  onActivateEvolution,
  onHandleComplete: onHandleCompleteProp,
}: Props) => {
  const [isCompleted, setIsCompleted] = useState<boolean>(completed);
  const [isHovered, setIsHovered] = useState(false);
  const { user } = useSelector(getUserSelector);
  const dispatch = useDispatch<AppDispatch>();

  const onHandleComplete = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!user.uuid) {
      dispatch(
        showModal({
          modalType: "login-required",
          freeFeatureLoggedInRequiredHook: "complete-evo",
        }),
      );
      return;
    }

    const shouldComplete = !isCompleted;
    setIsCompleted(shouldComplete);
    onHandleCompleteProp({ id, categoryId, completed: shouldComplete });
  };

  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return (
    <article
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={clsx(
        "relative group text-evolution-secondary pl-2 flex flex-col rounded-lg border-[1.5px] ",
        isCompleted
          ? "bg-gray-750 border-gray-750 h-fit cursor-default"
          : isActive
            ? "bg-evolution-active border-evolution-stroke"
            : "bg-evolution-primary border-evolution-primary desktop:hover:bg-evolution-hover desktop:hover:border-evolution-hover cursor-pointer",
      )}
      onClick={() => {
        if (!isCompleted && onActivateEvolution) {
          onActivateEvolution();
        }
      }}
    >
      <div
        className={clsx(
          "flex flex-row items-center justify-between whitespace-nowrap",
          expanded ? "w-full" : "w-max",
        )}
      >
        <div className="flex flex-col">
          <h2
            className={clsx(
              "text-sm py-0 my-0 leading-none mt-[7px]",
              isCompleted && "text-dark-disabled",
              isMobile && "text-xs",
            )}
          >
            {title}
          </h2>
          {isCompleted && (
            <span className="absolute top-6 text-xs text-dark-tertiary">
              Completed
            </span>
          )}
          <div
            className={clsx(
              "flex text-tiny touch-device:text-sm gap-x-2 gap-y-0",
              isCompleted && "invisible",
            )}
          >
            <SubtitleElem active={isActive || expanded}>
              <div className=" my-auto pb-[1px] w-[10px] h-[10px] touch-device:w-[14px] touch-device:h-[14px] fill-evolution-secondary ">
                {expireIcon}
              </div>
              <p className="text-xs">{timeRemaining}</p>
            </SubtitleElem>

            {cost?.coins && (
              <SubtitleElem active={isActive || expanded}>
                <img
                  alt={"fifapoint"}
                  className="pb-[1px] w-[10px] h-[10px] touch-device:w-[14px] touch-device:h-[14px] my-auto"
                  src={
                    import.meta.env.VITE_APP_ASSETS_BASE_URL +
                    "/fc24/misc/points.png"
                  }
                />
                <span className="my-auto text-xs">
                  {convertCost(cost.points)}
                </span>
              </SubtitleElem>
            )}
            {cost?.points && (
              <SubtitleElem active={isActive || expanded}>
                <img
                  alt={"fc24coin"}
                  className="pb-[1px] w-[9px] h-[10px] touch-device:w-[14px] touch-device:h-[14px] my-auto"
                  src={
                    import.meta.env.VITE_APP_ASSETS_BASE_URL +
                    "/fc24/misc/fc24coin.png"
                  }
                />
                <span className="text-xs">{convertCost(cost.coins)}</span>
              </SubtitleElem>
            )}
            {newEvolution && (
              <span
                className={clsx(
                  "my-auto text-xs text-[#4CFF9D] font-semibold",
                  isActive || expanded ? "opacity-95" : "opacity-75",
                )}
              >
                NEW
              </span>
            )}
            {processingState !== 3 && (
              <section
                className="flex flex-row items-center py-1 text-tiny gap-x-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Tooltip
                  content={
                    <div className="flex flex-col gap-y-4 text-[12px] py-4 w-[90vw] mobile:w-auto">
                      <div className="font-semibold text-white">
                        <div>
                          We are calculating stats for all applications of this
                          Evolution
                        </div>
                      </div>
                      <div className="flex flex-row text-white/75 gap-x-6">
                        <div
                          className={clsx(
                            "flex flex-col",
                            processingState >= 1
                              ? " gap-y-[36px]"
                              : "gap-y-[40px]",
                          )}
                        >
                          <div className="flex flex-col items-center gap-y-2">
                            <div
                              className={clsx(
                                "font-semibold desktop:whitespace-nowrap text-center",
                                processingState >= 1 && "text-[#11F46E]/75",
                              )}
                            >
                              1 step:
                            </div>
                            {processingState === 0 ? (
                              <LoadingDots />
                            ) : (
                              <div className="-mt-2 flex flex-col items-center w-5 h-5 bg-[#11F46E]/75 rounded-full text-[#062E23]">
                                {checkmark}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col items-center">
                            <div className="font-semibold desktop:whitespace-nowrap text-center">
                              Full path:
                            </div>
                            {processingState === 0 || processingState === 1 ? (
                              <div className="mt-2">
                                <LoadingDots />
                              </div>
                            ) : (
                              <PauseCircle />
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col gap-y-8">
                          <div>
                            <div
                              className={clsx(
                                processingState >= 1 && "text-[#11F46E]/75",
                              )}
                            >
                              Calculation of all basic cards with only this
                              evolution applied
                            </div>
                            <div className="flex flex-row mt-1">
                              <EvolutionNode label="Basic" />
                              <div className="w-3 h-[2px] bg-[#ECD882] my-auto" />
                              <EvolutionNode label="Evolution" />
                            </div>
                          </div>
                          <div>
                            <div className="mt-1">
                              Calculation of all basic cards with this evolution
                              combined with other available evolutions
                            </div>
                            <div className="flex flex-row mt-1">
                              <EvolutionNode label="Basic" />
                              <div className="w-3 h-[2px] bg-[#ECD882] my-auto" />
                              <EvolutionNode label="Evolution" />
                              <div className="w-3 h-[2px] bg-[#ECD882] my-auto" />
                              <EvolutionNode label="Evolution" />
                              <div className="w-3 h-[2px] bg-[#ECD882] my-auto" />
                              <EvolutionNode label="Evolution" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  }
                  side="bottom"
                  delayDuration={0}
                >
                  {processingState === 0 || processingState === 1 ? (
                    <div className="flex flex-row items-center bg-[#ECD882] rounded-[5px]  pl-[3px] pr-[2px] text-[#053A2D] gap-x-1 h-fit">
                      <span className="leading-4">Processing</span>
                      <span className="w-4 h-4">{infoIcon}</span>
                    </div>
                  ) : (
                    <div>
                      <InfoIcon
                        outerFill="var(--color-evolution-secondary)"
                        width="16"
                        height="16"
                      />
                    </div>
                  )}
                </Tooltip>
              </section>
            )}
          </div>
        </div>
        <div
          className={clsx(
            "w-[32px] h-[44px] flex items-center justify-center text-[#5D965E] hover:text-[#DAC56B] cursor-pointer",
            isCompleted && "text-dark-tertiary hover:text-dark-primary",
          )}
          onClick={onHandleComplete}
        >
          {!isMobile && !isCompleted && isHovered && (
            <div className={clsx("flex items-center justify-center")}>
              {evolutionCheckMarkIcon}
            </div>
          )}
          {(isMobile || isCompleted) && (
            <div className={clsx("flex items-center justify-center")}>
              {evolutionCheckMarkIcon}
            </div>
          )}
        </div>
      </div>
      {expanded && !isCompleted && (
        <>
          <section
            className={clsx("flex flex-row justify-between pr-2 gap-x-10 pt-2")}
          >
            <section className="flex flex-col gap-y-2">
              <span
                className={clsx(
                  "font-bold text-xs",
                  isMobile && expanded && "text-xxs",
                )}
              >
                Requirements
              </span>
              <ul>
                {requirements.map((r) => (
                  <li
                    key={r.label + r.value}
                    className={clsx(
                      "flex flex-row justify-between gap-x-4 whitespace-nowrap text-xs",
                      isMobile && expanded && "text-xxs",
                    )}
                  >
                    <span>{r.label}</span>
                    <span>{r.value}</span>
                  </li>
                ))}
              </ul>
            </section>
            <section className="flex flex-col gap-y-2">
              <span
                className={clsx(
                  "font-bold text-xs",
                  isMobile && expanded && "text-xxs",
                )}
              >
                Upgrades
              </span>
              <ul>
                {upgrades.map((u) => (
                  <li
                    key={u.label + u.value}
                    className={clsx(
                      "flex flex-row justify-between gap-x-4 whitespace-nowrap text-xs",
                      isMobile && expanded && "text-xxs",
                    )}
                  >
                    <span>{u.label}</span>
                    <span>{u.value}</span>
                  </li>
                ))}
              </ul>
            </section>
          </section>
        </>
      )}
    </article>
  );
};

const SubtitleElem = ({
  children,
  active,
}: {
  children: React.ReactNode;
  active: boolean;
}) => (
  <div
    className={clsx(
      "flex my-auto gap-x-[2px]",
      active ? "opacity-75" : "opacity-60",
    )}
  >
    {children}
  </div>
);

export default EvolutionsCard;
